import { useEventBus } from '@vueuse/core';
import { reactive, ref, toRaw, type Ref } from 'vue';
import { Assert } from '../../assert';
import { isDebugEnabled } from 'shared/common/debug';

export type EntityChanged<T> = EntityUpdated<T> | EntityRemoved;

export interface EntityUpdated<T> {
  id: string;
  action: 'updated';
  data: T;
}

export interface EntityRemoved {
  id: string;
  action: 'removed';
}

export interface EntityStoreOptions<T> {
  eventName: string;
  getEntityId: (entity: T) => string;
  updateEntity: (dest: T, src: T) => void;
  insertEntity?: (entity: T) => void;
  beforeUpdateEntity?: (entity: T) => void;
  afterUpdateEntity?: (entity: T) => void;
  removeEntity?: (entity: T) => void;
  hasChanged?: (existing: T, incoming: T) => boolean;
}

export function useEntityStore<T extends object>(options: EntityStoreOptions<T>) {
  const {
    getEntityId,
    insertEntity,
    updateEntity,
    removeEntity,
    beforeUpdateEntity,
    afterUpdateEntity,
  } = options;

  const list = ref<T[]>([]) as Ref<T[]>;
  const byId = new Map<string, T>();
  const changed = useEventBus<EntityChanged<T>[]>(options.eventName);

  let emitCount = 0;
  const originalEmit = changed.emit.bind(changed);

  changed.emit = (payload?: EntityChanged<T>[], extraPayload?: any) => {
    emitCount++;

    if (merging && payload) {
      batchedChanges.push(...payload);
    } else {
      return originalEmit(payload, extraPayload);
    }
  };

  let merging = false;
  let batchedChanges: EntityChanged<T>[] = [];
  let perfDepth = 0;

  function startPerf(): number {
    perfDepth++;

    return perfDepth === 1 && !merging && isDebugEnabled('storePerf') ? performance.now() : -1;
  }

  function endPerf(
    outcome: string,
    t0: number,
    emitsBefore: number,
    id: string,
    changes?: Record<string, { before: unknown; after: unknown }>,
  ) {
    perfDepth--;

    if (t0 < 0) return;

    const totalMs = performance.now() - t0;
    const emits = emitCount - emitsBefore;

    const message =
      `[${options.eventName}] ${outcome} ${id}: ` +
      `${totalMs.toFixed(1)}ms, ${emits} emits`;

    if (changes && Object.keys(changes).length > 0) {
      console.log(message, changes);
    } else {
      console.log(message);
    }
  }

  function getById(id: string) {
    return byId.get(id);
  }

  function getOrThrow(id: string) {
    const entity = getById(id);
    Assert.definied(entity);
    return entity;
  }

  function add(entity: T): 'inserted' | 'updated' | 'unchanged' {
    const t0 = startPerf();
    const emits0 = emitCount;
    const entityId = getEntityId(entity);

    let outcome: 'inserted' | 'updated' | 'unchanged' | 'failed' = 'failed';
    let changes: Record<string, { before: unknown; after: unknown }> | undefined;

    try {
      const existing = getById(entityId);

      if (!existing) {
        const newEntity = reactive(entity) as T;

        list.value.push(newEntity);
        byId.set(entityId, newEntity);

        insertEntity?.(newEntity);

        changed.emit([
          {
            id: entityId,
            action: 'updated',
            data: newEntity,
          },
        ]);

        outcome = 'inserted';
      } else if (options.hasChanged && !options.hasChanged(existing, entity)) {
        outcome = 'unchanged';
      } else {
        const before = isDebugEnabled('storePerf') ? structuredClone(toRaw(existing)) : undefined;

        update(existing, (e) => updateEntity(e, entity));

        if (before) {
          changes = getChangedFields(before, toRaw(existing));
        }

        outcome = 'updated';
      }

      return outcome;
    } finally {
      endPerf(outcome, t0, emits0, entityId, changes);
    }
  }

  function update(entity: T, action: (entity: T) => void) {
    const t0 = startPerf();
    const emits0 = emitCount;
    const id = getEntityId(entity);
    let outcome = 'failed';

    try {
      beforeUpdateEntity?.(entity);
      action(entity);
      afterUpdateEntity?.(entity);
      changed.emit([{ id, action: 'updated', data: entity }]);
      outcome = 'updated';
    } finally {
      endPerf(outcome, t0, emits0, id);
    }
  }

  function remove(id: string) {
    const t0 = startPerf();
    const emits0 = emitCount;
    let outcome = 'not found';

    try {
      const index = list.value.findIndex((e) => getEntityId(e) === id);
      if (index < 0) return false;

      const entity = list.value[index];
      if (!entity) return false;

      list.value.splice(index, 1);
      byId.delete(id);
      removeEntity?.(entity);
      changed.emit([{ id, action: 'removed' }]);
      outcome = 'removed';
      return true;
    } finally {
      endPerf(outcome, t0, emits0, id);
    }
  }

  function merge(entities: T[]) {
    const perfDebug = isDebugEnabled('storePerf');
    const t0 = perfDebug ? performance.now() : 0;
    emitCount = 0;
    merging = true;
    batchedChanges = []; // Clear the batch at the start

    try {
      const ids = new Set<string>();
      const inserted: T[] = [];

      for (const entity of entities) {
        const id = getEntityId(entity);
        ids.add(id);
        const existing = byId.get(id);

        if (!existing) {
          const newEntity = reactive(entity) as T;
          byId.set(id, newEntity);
          inserted.push(newEntity);
          insertEntity?.(newEntity);
        } else if (!options.hasChanged || options.hasChanged(existing, entity)) {
          // This calls update(), which triggers changed.emit([{...}]).
          // Our wrapper safely catches it and pushes to batchedChanges.
          update(existing, (e) => updateEntity(e, entity));
        }
      }

      if (inserted.length > 0) {
        list.value.push(...inserted);
        // Map inserted items to an array of changes and emit once
        const insertPayload: EntityChanged<T>[] = inserted.map((e) => ({
          id: getEntityId(e),
          action: 'updated',
          data: e,
        }));
        changed.emit(insertPayload);
      }

      const removedIds: string[] = [];
      for (const id of byId.keys()) {
        if (!ids.has(id)) removedIds.push(id);
      }

      if (removedIds.length > 0) {
        const removeSet = new Set(removedIds);
        const removedEntities = removedIds.map((id) => byId.get(id)!);

        list.value = list.value.filter((e) => !removeSet.has(getEntityId(e)));

        const removePayload: EntityChanged<T>[] = removedEntities.map((entity) => {
          const id = getEntityId(entity);
          byId.delete(id);
          removeEntity?.(entity);
          return { id, action: 'removed' };
        });

        changed.emit(removePayload);
      }
    } finally {
      merging = false;

      // Fire the single batched emit containing everything
      if (batchedChanges.length > 0) {
        originalEmit(batchedChanges);
        batchedChanges = [];
      }

      if (perfDebug) {
        console.log(
          `[${options.eventName}] merge time: ${(performance.now() - t0).toFixed(1)}ms, ` +
            `${entities.length} merged, ${emitCount} emits`,
        );
      }
    }
  }

  function clear() {
    const ids = list.value.map((e) => getEntityId(e));
    list.value.length = 0;
    byId.clear();

    if (ids.length > 0) {
      const removePayload: EntityChanged<T>[] = ids.map((id) => ({
        id,
        action: 'removed',
      }));
      changed.emit(removePayload);
    }

    return ids;
  }

  function getChangedFields(
    before: unknown,
    after: unknown,
    path = '',
    changes: Record<string, { before: unknown; after: unknown }> = {},
  ) {
    // Same primitive / same reference
    if (Object.is(before, after)) {
      return changes;
    }

    // Handle arrays
    if (Array.isArray(before) && Array.isArray(after)) {
      const length = Math.max(before.length, after.length);

      for (let i = 0; i < length; i++) {
        const childPath = path ? `${path}[${i}]` : `[${i}]`;

        if (i >= before.length) {
          changes[childPath] = {
            before: undefined,
            after: after[i],
          };
        } else if (i >= after.length) {
          changes[childPath] = {
            before: before[i],
            after: undefined,
          };
        } else {
          getChangedFields(before[i], after[i], childPath, changes);
        }
      }

      return changes;
    }

    // Handle objects
    if (
      before !== null &&
      after !== null &&
      typeof before === 'object' &&
      typeof after === 'object' &&
      !Array.isArray(before) &&
      !Array.isArray(after)
    ) {
      const keys = new Set([...Object.keys(before), ...Object.keys(after)]);

      for (const key of keys) {
        const childPath = path ? `${path}.${key}` : key;

        getChangedFields(
          (before as Record<string, unknown>)[key],
          (after as Record<string, unknown>)[key],
          childPath,
          changes,
        );
      }

      return changes;
    }

    // Value changed
    changes[path] = {
      before,
      after,
    };

    return changes;
  }

  return {
    getById,
    add,
    remove,
    merge,
    clear,
    getOrThrow,
    update,
    list,
    changed,
  };
}
